/**
 * Spaces as elements — the node-universe materialization of `space`. (Design §1,
 * plan P0.3.)
 *
 * `model.spaces` is a `Map<fileUri, QualifiedName>` — a file TAG, not an element.
 * The v0.10 selector core needs spaces to be first-class members of the node
 * universe: the `space` kind atom enumerates them, `in <Space>` tests
 * membership, and a space subject's `in this` yields its structural descendants
 * (design §1's kind-polymorphic dispatch). This module materializes those
 * entities + membership without inventing syntax the parser (P1a) does not yet
 * emit — the P1b evaluator wires `buildSpaceIndex` into its universe.
 *
 * ## Identity & membership
 *
 * A space's identity is its qualified name (the `package:` declaration). An
 * element BELONGS to a space `S` iff its declaring space is `S` or a descendant
 * of `S` (the subtree rule) — so `in example.shop` sees modules declared in
 * `example.shop.orders`. Membership is derived from the declaring FILE, so it is
 * **scope-invariant**: a deeply-nested module belongs to its file's space
 * exactly like a top-level one (nesting is lexical, within one file).
 *
 * Spaces are **never edge endpoints** (design §1) — that rule is a selector-time
 * targeted diagnostic (P1b), not a materialization concern; nothing here places
 * a space on an edge.
 *
 * Entities are the DECLARED spaces (distinct values of `model.spaces`). Ancestor
 * prefixes are meaningful for membership queries (`spaceContains` accepts any
 * query space) but are not themselves materialized as entities unless declared —
 * whether the `space` atom should also enumerate undeclared prefixes is a P1b
 * call, deferred until there is an evaluator to consume it.
 */

import type { QualifiedName } from "./types.js";
import type { ResolvedModel, ResolvedModule } from "./resolver.js";
import { spaceOfModule } from "./hoist-index.js";

/**
 * Subtree membership: an element declared in `elementSpace` is within `space`
 * iff `space` is a prefix of `elementSpace` (the space itself, or any ancestor).
 * A package-root element (`elementSpace === undefined`) is in no declared space.
 */
export function spaceContains(space: QualifiedName, elementSpace: QualifiedName | undefined): boolean {
  if (!elementSpace || space.length > elementSpace.length) return false;
  for (let i = 0; i < space.length; i++) if (space[i] !== elementSpace[i]) return false;
  return true;
}

export interface SpaceIndex {
  /** Distinct declared spaces (values of `model.spaces`), sorted by dotted key. */
  readonly spaces: readonly QualifiedName[];
  /** Dotted keys parallel to {@link spaces} — the atom/`in` reference form. */
  readonly keys: readonly string[];
  /** The declaring space of a module (via its source file); `undefined` at root. */
  spaceOfModule(m: ResolvedModule): QualifiedName | undefined;
  /** The declaring space of any element, by its source file. */
  spaceOfFile(file: string | undefined): QualifiedName | undefined;
  /** Every module (recursively, all nesting depths) whose declaring space is
   *  within `space` — the `in <Space>` / space-subject `in this` member set. */
  modulesIn(space: QualifiedName): ResolvedModule[];
  /** The subtree membership predicate (re-exported as a method for convenience). */
  contains(space: QualifiedName, elementSpace: QualifiedName | undefined): boolean;
}

export function buildSpaceIndex(model: ResolvedModel): SpaceIndex {
  const spacesMap = model.spaces;

  // Every module at every nesting depth — nesting is lexical (one file), so a
  // child shares its parent's declaring space.
  const allModules: ResolvedModule[] = [];
  const walk = (m: ResolvedModule): void => {
    allModules.push(m);
    for (const c of m.modules) walk(c);
  };
  for (const m of model.modules) walk(m);

  // Distinct declared spaces (dedup by dotted key; single-space / package-root
  // models have none → an empty universe, the `space` atom is a no-op).
  const byKey = new Map<string, QualifiedName>();
  if (spacesMap) for (const qn of spacesMap.values()) if (qn.length > 0) byKey.set(qn.join("."), qn);
  const keys = [...byKey.keys()].sort();
  const spaces = keys.map((k) => byKey.get(k)!);

  const spaceOf = (m: ResolvedModule): QualifiedName | undefined => spaceOfModule(m, spacesMap);

  return {
    spaces,
    keys,
    spaceOfModule: spaceOf,
    spaceOfFile: (file) => (file && spacesMap ? spacesMap.get(file) : undefined),
    contains: spaceContains,
    modulesIn: (space) => allModules.filter((m) => spaceContains(space, spaceOf(m))),
  };
}
